import React, { useState, useRef, useCallback } from 'react';
import './FileUpload.css';
import { S3Service } from '../services/s3Service';

interface FileUploadProps {
  onUploadStart: () => void;
  onUploadProgress: (progress: number) => void;
  onUploadComplete: (fileUrl: string) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onUploadStart,
  onUploadProgress,
  onUploadComplete
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setSelectedFile(files[0]);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      console.log('No file selected for upload.');
      return;
    }

    console.log('Starting upload for file:', selectedFile);
    onUploadStart();
    setUploadStatus('Uploading...');

    try {
      const { fileUrl, key } = await S3Service.uploadFile(selectedFile, (progress) => {
        const percent = Math.round((progress.loaded / progress.total) * 100);
        console.log(`Upload progress: ${percent}%`, progress);
        onUploadProgress(percent);
      });

      // Use the returned fileUrl and key
      console.log('Upload completed. File URL:', fileUrl, 'S3 Key:', key);
      onUploadComplete(fileUrl);
      setUploadStatus('Upload completed successfully!');

      setTimeout(() => {
        setSelectedFile(null);
        setUploadStatus('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 2000);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus('Upload failed. Please try again.');
      onUploadProgress(0);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="file-upload">
      <div
        className={`upload-area ${isDragOver ? 'drag-over' : ''} ${selectedFile ? 'has-file' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          accept="*/*"
        />
        
        <div className="upload-content">
          <div className="upload-icon">
            📁
          </div>
          {selectedFile ? (
            <div className="file-info">
              <h3>Selected File</h3>
              <p className="file-name">{selectedFile.name}</p>
              <p className="file-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          ) : (
            <div className="upload-text">
              <h3>Drop your file here</h3>
              <p>or click to browse</p>
            </div>
          )}
        </div>
      </div>

      {selectedFile && (
        <div className="upload-actions">
          <button 
            className="upload-button"
            onClick={handleUpload}
            disabled={uploadStatus === 'Uploading...'}
          >
            {uploadStatus === 'Uploading...' ? 'Uploading...' : 'Upload to S3'}
          </button>
          <button 
            className="clear-button"
            onClick={() => {
              setSelectedFile(null);
              setUploadStatus('');
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}
          >
            Clear
          </button>
        </div>
      )}

      {uploadStatus && (
        <div className={`upload-status ${uploadStatus.includes('failed') ? 'error' : 'success'}`}>
          {uploadStatus}
        </div>
      )}
    </div>
  );
};

export default FileUpload; 